import { Group, Texture, CanvasTexture } from 'three';
import { ShaftSurfaceTexture } from '@app/react/const/type';
import { PartsMesh } from './PartsMesh';
import { PartsMeshs } from './PartsMeshs';
import { KintekiArrowModel } from './ArrowModelInterface';
import { OrdermadeArrowModel } from './ordermadeArrowModel';
import { writeVertical, writeHorizontal } from './carvingWriter';
import { changeShiny } from './changeShininess';

export interface KintekiParts {
    shaft: PartsMeshs;
    haneFront: Array<PartsMeshs>;
    haneBack: Array<PartsMeshs>;
    threads: Array<PartsMeshs>;
    kebikis: Array<PartsMeshs>
    tenkyuHazu: PartsMesh;
    hukumiHazu: PartsMesh;
    shortYajiri: PartsMesh;
    longYajiri: PartsMesh;
    hazumakiFix: PartsMesh;
    carving: PartsMesh;
    familycrest: Array<PartsMesh>;
    hanaka: PartsMesh;
    lame: Array<PartsMesh>;
    deco: Array<PartsMesh>;

    canvas: HTMLCanvasElement;
    context: CanvasRenderingContext2D | null;
    tex: CanvasTexture;
}

export class KintekiOrdermadeArrowModel extends OrdermadeArrowModel implements KintekiArrowModel {
    private shaft: PartsMeshs | null;

    private haneFront: Array<PartsMeshs>;

    private haneBack: Array<PartsMeshs>;

    private threads: Array<PartsMeshs>;

    private kebikis: Array<PartsMeshs>;

    private tenkyuHazu: PartsMesh | null;

    private hukumiHazu: PartsMesh | null;

    public shortYajiri: PartsMesh | null;

    private longYajiri: PartsMesh | null;

    private hazumakiFix: PartsMesh | null;

    public carving: PartsMesh | null;

    private familycrest: Array<PartsMesh>;

    public hanaka: PartsMesh | null;

    private lame: Array<PartsMesh>;

    private deco: Array<PartsMesh>;

    private canvas: HTMLCanvasElement | null;

    private context: CanvasRenderingContext2D | null;

    public constructor(model: Group, parts?: KintekiParts) {
        super(model);

        this.shaft = parts?.shaft || null;
        this.haneFront = parts?.haneFront || [];
        this.haneBack = parts?.haneBack || [];
        this.threads = parts?.threads || [];
        this.kebikis = parts?.kebikis || [];
        this.tenkyuHazu = parts?.tenkyuHazu || null;
        this.hukumiHazu = parts?.hukumiHazu || null;
        this.shortYajiri = parts?.shortYajiri || null;
        this.longYajiri = parts?.longYajiri || null;
        this.hazumakiFix = parts?.hazumakiFix || null;
        this.carving = parts?.carving || null;
        this.familycrest = parts?.familycrest || [];
        this.hanaka = parts?.hanaka || null;
        this.lame = parts?.lame || [];
        this.deco = parts?.deco || [];

        this.canvas = parts?.canvas || null;
        this.context = parts?.context || null;
        if (parts?.tex !== undefined) {
            this.setCarving(parts.tex);
        }

        this.showHukumi();
    }

    public reset(): void {
        this.shaft?.reset();
        this.haneFront.forEach((f) => f.reset());
        this.haneBack.forEach((f) => f.reset());
        this.resetThread();
        this.resetKebiki();
        this.tenkyuHazu?.reset();
        this.hukumiHazu?.reset();
        this.shortYajiri?.reset();
        this.longYajiri?.reset();
        this.hazumakiFix?.reset();
        this.writeHorizontal('');
        this.familycrest.forEach((f) => f.reset());
        this.hanaka?.reset();
        this.lame.forEach((f) => f.reset());
        this.deco.forEach((f) => f.reset());
        this.showHukumi();
    }

    public resetShaft(): void {
        this.shaft?.reset();
    }

    public resetFeather(): void {
        this.haneFront.forEach((f) => f.reset());
        this.haneBack.forEach((f) => f.reset());
    }

    public resetThread(): void {
        this.threads.forEach((f) => { f.reset(); });
    }

    public resetKebiki(): void {
        this.kebikis.forEach((f) => { f.reset(); });
    }

    public resetNock(): void {
        this.tenkyuHazu?.reset();
        this.hukumiHazu?.reset();
    }

    public resetArrowhead(): void {
        this.shortYajiri?.reset();
        this.longYajiri?.reset();
    }

    public resetTwine(): void {
        this.hazumakiFix?.reset();
    }

    public resetLame(): void {
        this.lame.forEach((f) => f.reset());
    }

    public resetFamilycrest(): void {
        this.familycrest.forEach((f) => f.reset());
    }

    public resetFeatherinside(): void {
        this.hanaka?.reset();
    }

    public resetDecoration(): void {
        this.deco.forEach((f) => f.reset());
    }

    // CHANGE TEXTURE

    public setShaft(tex: Texture, type: ShaftSurfaceTexture): void {
        OrdermadeArrowModel.applyTextureParamWithShaft(tex);
        if (this.shaft) {
            this.shaft.mat.map = tex;
            changeShiny(this.shaft.mat, type);
        }
    }

    public setFeather(front: Texture, back: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(front);
        OrdermadeArrowModel.applyTextureParam1(back);
        this.haneFront.forEach((f) => { f.mat.map = front; });
        this.haneBack.forEach((f) => { f.mat.map = back; });
    }

    public setThread(tex: Texture, gloss: ShaftSurfaceTexture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.threads.forEach((f) => {
            f.mat.map = tex;
            changeShiny(f.mat, gloss);
        });
    }

    public setKebiki(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.kebikis.forEach((f) => { f.mat.map = tex; });
    }

    public setNock(tex: Texture, alpha: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        OrdermadeArrowModel.applyTextureParam1(alpha);
        if (this.hukumiHazu) {
            this.hukumiHazu.mat.map = tex;
            this.hukumiHazu.mat.alphaMap = alpha;
        }
        if (this.tenkyuHazu) {
            this.tenkyuHazu.mat.map = tex;
            this.tenkyuHazu.mat.alphaMap = alpha;
        }
    }

    public setArrowhead(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.shortYajiri) this.shortYajiri.mat.map = tex;
        if (this.longYajiri) this.longYajiri.mat.map = tex;
    }

    public setTwine(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.hazumakiFix) this.hazumakiFix.mat.alphaMap = tex;
    }

    public setTwineColor(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.hazumakiFix) this.hazumakiFix.mat.map = tex;
    }

    public setCarving(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.carving) this.carving.mat.alphaMap = tex;
    }

    public setCarvingColor(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.carving) this.carving.mat.map = tex;
    }

    public setFamilycrest(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.familycrest.forEach((f) => { f.mat.alphaMap = tex; });
    }

    public setFamilycrestColor(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.familycrest.forEach((f) => { f.mat.map = tex; });
    }

    public setFeatherinside(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.hanaka) this.hanaka.mat.alphaMap = tex;
    }

    public setFeatherinsideColor(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (this.hanaka) this.hanaka.mat.map = tex;
    }

    public setLame(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.lame.forEach((f) => { f.mat.alphaMap = tex; });
    }

    public setLameColor(tex: Texture): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        this.lame.forEach((f) => { f.mat.map = tex; });
    }

    public setDecoration(tex: Texture, index?: 0 | 1 | 2): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (index === undefined) {
            this.deco.forEach((f) => {
                f.mat.alphaMap = tex;
            });
            return;
        }

        const t = this.deco[index];
        if (t === undefined) {
            console.error('[OrdermadeArrow::decoration] index out of range:', tex, index);
            return;
        }
        t.mat.alphaMap = tex;
    }

    public setDecorationColor(tex: Texture, index?: 0 | 1 | 2): void {
        OrdermadeArrowModel.applyTextureParam1(tex);
        if (index === undefined) {
            this.deco.forEach((f) => { f.mat.map = tex; });
            return;
        }

        const t = this.deco[index];
        if (t === undefined) {
            console.error('[OrdermadeArrow::decorationColor] index out of range:', tex, index);
            return;
        }
        t.mat.map = tex;
    }

    // VISIBLE

    public showHukumi(): void {
        this.hukumiHazu?.show();
        this.tenkyuHazu?.hide();
    }

    public showTenkyu(): void {
        this.hukumiHazu?.hide();
        this.tenkyuHazu?.show();
    }

    public showLongYajiri(): void {
        this.longYajiri?.show();
    }

    public showShortYajiri(): void {
        this.longYajiri?.hide();
    }

    // Texture Paramter

    public setThreadShiny(gloss: ShaftSurfaceTexture): void {
        this.threads.forEach((f) => changeShiny(f.mat, gloss));
    }

    // Carving

    public writeVertical(text: string, font = "25px 'ＭＳ Ｐゴシック'", stroke = false): void {
        if (this.canvas === null || this.context === null) {
            console.error('canvas null');
            return;
        }

        writeVertical(
            this.context,
            this.canvas.width,
            this.canvas.height,
            text,
            font,
            stroke,
        );

        this.updateCarving();
    }

    public writeHorizontal(text: string, font = "25px 'Arial'"): void {
        if (this.canvas === null || this.context === null) {
            console.error('canvas null');
            return;
        }

        writeHorizontal(
            this.context,
            this.canvas.width,
            this.canvas.height,
            text,
            font,
        );

        this.updateCarving();
    }

    private updateCarving(): void {
        if (this.carving?.mat.alphaMap !== null) {
            this.carving.mat.alphaMap.needsUpdate = true; // needed by canvas texture
        }
    }
}
